/**
 * Hive's binding of the shared chat widget.
 *
 * The loader, the restore rule and the SDK hand-off live in `pb-chat-widget`,
 * which hive, checkout and the partners portal all mount. What is app-specific
 * is where the two runtime values come from — the `public.pb` config and the
 * request-scoped shared flag — plus the two support-lab facts below, which no
 * other app has. Both runtime values are read inside a function, at call time,
 * because they need the Nuxt instance an await or a module-level read would not
 * have.
 *
 * The named exports below are hive's published surface: the auth store, the POA
 * page and the lab harness reach chat through them. `supportChat` is the same
 * object, handed whole to the launcher component, which needs the bound API
 * rather than the loose functions.
 */
import { defineChatWidget, isCustomerSessionCookie, type ChatWidgetPublicConfig } from 'pb-chat-widget';

export const supportChat = defineChatWidget({
    config: () => useRuntimeConfig().public.pb as ChatWidgetPublicConfig,
    sharedFlag: (key) => useState<boolean>(key, () => false),
    dev: import.meta.dev,
});

export const {
    chatWidgetEnvironment,
    loadChatWidget,
    useSdkOwnsLauncher,
    openSupportChat,
    hasExistingConversation,
    restoreSupportChat,
    resetSupportChat,
} = supportChat;

export {
    SDK_HOST_ID,
    notifySupportChatIdentityChanged,
    setSdkHostVisible,
    tearDownSdkWidget,
} from 'pb-chat-widget';

export { isCustomerSessionCookie };

export type { ChatWidgetEnvironment } from 'pb-chat-widget';

/**
 * Does the support lab currently own the widget?
 *
 * Its routes are the one place that drives the SDK deliberately and decides
 * which identity is under test, so while this is set the app-level launcher
 * keeps its hands off the widget's visibility. Every other rule about where the
 * widget may appear still applies everywhere else — this is the harness saying
 * "mine", not a blanket exemption.
 *
 * Hive's alone: no other app has a lab, and a shared package has no business
 * knowing one exists.
 */
export function useLabOwnsChat() {
    return useState<boolean>('pb-chat-lab-owns', () => false);
}

/**
 * Would the SDK treat this browser as a signed-in customer?
 *
 * The JWT rule is shared — it has to give the same answer chat-sdk's
 * `passportSessionToken()` gives — but which cookie carries it is hive's own
 * environment-suffixed name, so the lookup stays here and only the rule is
 * imported.
 */
export function hasChatCustomerSession(): boolean {
    if (typeof document === 'undefined') return false;
    const { tokenCookieName } = useAuthCookieNames();
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${tokenCookieName.replace(/[.+*]/g, '\\$&')}=([^;]*)`),
    );

    return isCustomerSessionCookie(match ? decodeURIComponent(match[1] ?? '') : null);
}
